/**
 * Parse `DATABASE_URL`, failing with a message an operator can act on.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * Six call sites wrote `new URL(process.env.DATABASE_URL ?? '')`. The `?? ''`
 * reads as a guard but is not one: `new URL('')` throws exactly as loudly as
 * `new URL(undefined)`, and the message it throws is the bare string
 * "Invalid URL" — no variable named, no file, no hint.
 *
 * That surfaced during a **restore**, which is the worst moment to hand someone
 * an error that points nowhere: production has no `.env` (PM2 supplies the
 * environment directly, see load-env.ts), so a missing or malformed variable
 * turns a recovery into a guessing game. The fault is trivial to fix once named
 * and near-impossible to find when not.
 *
 * Throws rather than returning null because every caller here needs a live
 * connection string to do anything at all; `LocalBackupService.connection()`
 * keeps its nullable form because it feeds a capability check.
 */
export function databaseUrl(): URL {
  const raw = process.env.DATABASE_URL;

  if (!raw) {
    throw new Error(
      'DATABASE_URL is not set, so there is no cluster to connect to. In production PM2 ' +
        'supplies it from the deploy workflow; locally it comes from .env.'
    );
  }

  try {
    return new URL(raw);
  } catch {
    // Never echo the value back — it carries the database password.
    throw new Error(
      `DATABASE_URL is set but is not a valid URL (${raw.length} characters). It should look ` +
        'like postgresql://user:password@host:5432/database.'
    );
  }
}
